import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Bird, Eye, EyeSlash, Clock, Warning } from '@phosphor-icons/react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendienteAprobacion, setPendienteAprobacion] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Por favor complete todos los campos');
      return;
    }

    setLoading(true);
    setPendienteAprobacion(false);
    try {
      await login(email, password);
      toast.success('¡Bienvenido!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al iniciar sesión';
      // Check if account is pending approval
      if (error.response?.status === 403 && message.includes('pendiente de aprobación')) {
        setPendienteAprobacion(true);
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_chicken-cost-tracker-1/artifacts/d7jt8vh8_LOGO%20ELANCO%20GROUP%20MARCA%20DE%20AGUA.png"
            alt="Elanco Group"
            className="h-24 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold tracking-tight text-slate-800 uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Sistema de Gestión Avícola
          </h1>
        </div>

        <Card className="border-border/60 shadow-lg bg-white">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl text-center" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Iniciar Sesión
            </CardTitle>
            <CardDescription className="text-center">
              Ingrese sus credenciales para acceder
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Mensaje de cuenta pendiente */}
            {pendienteAprobacion && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg animate-fade-in">
                <div className="flex items-start gap-3">
                  <Clock size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Cuenta pendiente de aprobación</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Su registro está siendo revisado por un administrador. 
                      Por favor, espere a que su cuenta sea aprobada para poder iniciar sesión.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Correo Electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="correo@ejemplo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-focus"
                  data-testid="login-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-focus pr-10"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#E73425] hover:bg-[#D81C23] btn-animate"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <div className="spinner h-5 w-5" />
                ) : (
                  'Ingresar'
                )}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">¿No tiene una cuenta? </span>
              <Link
                to="/register"
                className="text-[#E73425] hover:text-[#D81C23] font-medium"
                data-testid="register-link"
              >
                Registrarse
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-6">
          © 2024 Elanco Group. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
}
